// firebase.ts
import type { FirebaseApp } from "firebase/app";
import { initializeApp } from "firebase/app";
import type { Messaging } from "firebase/messaging";
import { getMessaging, onMessage } from "firebase/messaging";

const REQUIRED_KEYS = [
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_APP_ID",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
] as const;

function getFirebaseConfig() {
  const env = import.meta.env;
  const config = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };
  const hasRequired = REQUIRED_KEYS.every(
    (key) => env[key] != null && String(env[key]).trim() !== "",
  );
  return { config, hasRequired };
}

let app: FirebaseApp | null = null;
let messagingInstance: Messaging | null = null;

/**
 * Returns Firebase Messaging instance, or null if config is missing/invalid.
 * Initializes Firebase on first call when env vars are set.
 */
export function getMessagingInstance(): Messaging | null {
  if (messagingInstance != null) {
    return messagingInstance;
  }
  const { config, hasRequired } = getFirebaseConfig();
  if (!hasRequired) {
    return null;
  }
  app = initializeApp(config);
  messagingInstance = getMessaging(app);
  onMessage(messagingInstance, handleForegroundPayload);
  return messagingInstance;
}

export type ForegroundPayload = {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
};

let onForegroundNotification: ((payload: ForegroundPayload) => void) | null = null;

/**
 * Register a handler for foreground FCM messages (e.g. from React with useToast).
 * Call with null to unregister. Only one handler is active at a time.
 */
export function setForegroundNotificationHandler(
  handler: ((payload: ForegroundPayload) => void) | null,
) {
  onForegroundNotification = handler;
}

function handleForegroundPayload(payload: ForegroundPayload) {
  if (onForegroundNotification) {
    onForegroundNotification(payload);
  }
}

// Foreground path: SW posts FCM payload when a window is focused (no Firebase init required)
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "FCM_FOREGROUND" && event.data.payload) {
      handleForegroundPayload(event.data.payload);
    }
  });
}
